import type { TourTarget } from "../Constants/tutorialSteps";
import { tourTargetSelector } from "../Constants/tutorialSteps";
import { clampTourRectToViewport } from "./tutorialViewport";

const TARGET_WAIT_MS = 8000;
const TARGET_POLL_MS = 80;
const SPOTLIGHT_PAD = 8;

export function isTourTargetVisible(element: HTMLElement | null): boolean {
  if (!element) return false;
  if (element.closest("[hidden]")) return false;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function waitForTourTarget(
  target: TourTarget | undefined,
): Promise<HTMLElement | null> {
  if (!target) {
    return Promise.resolve(null);
  }

  const selector = tourTargetSelector(target);

  return new Promise((resolve) => {
    const started = performance.now();

    const tryFind = () => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element && isTourTargetVisible(element)) {
        resolve(element);
        return;
      }
      if (performance.now() - started >= TARGET_WAIT_MS) {
        resolve(element);
        return;
      }
      window.setTimeout(tryFind, TARGET_POLL_MS);
    };

    tryFind();
  });
}

export function scrollTourTargetIntoView(
  element: HTMLElement | null,
  block: ScrollLogicalPosition = "center",
): void {
  if (!element) return;
  element.scrollIntoView({ block, inline: "nearest", behavior: "smooth" });
}

export type TourRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export function measureTourTarget(
  element: HTMLElement | null,
  options?: { maxHeight?: number; maxWidth?: number; pad?: number },
): TourRect | null {
  if (!element || !isTourTargetVisible(element)) return null;
  return measureTourTargetElements([element], options);
}

/** Union bounding boxes when multiple nodes share the same `data-tour` (e.g. sibling buttons). */
export function measureTourTargetElements(
  elements: ArrayLike<HTMLElement | null | undefined>,
  options?: { maxHeight?: number; maxWidth?: number; pad?: number },
): TourRect | null {
  const visible = Array.from(elements).filter(
    (element): element is HTMLElement =>
      Boolean(element) && isTourTargetVisible(element as HTMLElement),
  );
  if (visible.length === 0) return null;

  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const element of visible) {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 && rect.height <= 0) continue;
    top = Math.min(top, rect.top);
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
  }
  if (!Number.isFinite(top) || right <= left || bottom <= top) return null;

  const pad = options?.pad ?? SPOTLIGHT_PAD;
  let width = right - left + pad * 2;
  if (options?.maxWidth != null) {
    width = Math.min(width, options.maxWidth + pad * 2);
  }

  const height = options?.maxHeight
    ? Math.min(bottom - top, options.maxHeight) + pad * 2
    : bottom - top + pad * 2;

  return clampTourRectToViewport({
    top: top - pad,
    left: left - pad,
    width,
    height,
  });
}

/** Wait until a settings (or other collapsible) panel is visible after expand. */
export async function waitForTourPanel(
  panelTarget: TourTarget,
  timeoutMs = TARGET_WAIT_MS,
): Promise<HTMLElement | null> {
  const selector = tourTargetSelector(panelTarget);
  const started = performance.now();

  return new Promise((resolve) => {
    const tryFind = () => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element && isTourTargetVisible(element)) {
        resolve(element);
        return;
      }
      if (performance.now() - started >= timeoutMs) {
        resolve(element);
        return;
      }
      window.setTimeout(tryFind, TARGET_POLL_MS);
    };
    tryFind();
  });
}
